import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs.js';
import { Badge, Banner, Button, Select, Skeleton } from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { useMe } from '../../hooks/useMe.js';
import type { User, UserStatus } from '../../lib/types.js';
import { useUserDetailLogic } from './useUserDetailLogic.js';
import { useUserGroupsLogic } from './useUserGroupsLogic.js';
import { useUserAdminRolesLogic } from './useAdminRolesLogic.js';
import { RoleSelector } from '../../components/RoleSelector.js';
import { useTeamsAppLogic } from './useTeamsAppLogic.js';
import { useGroupsListLogic } from '../groups/useGroupsLogic.js';

const statusVariant: Record<UserStatus, 'success' | 'warning' | 'danger'> = {
  ACTIVE: 'success',
  PENDING: 'warning',
  DEACTIVATED: 'danger',
};

const TabButton = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
      active ? 'border-primary text-ink' : 'border-transparent text-ink-muted hover:text-ink'
    }`}
  >
    {children}
  </button>
);

/**
 * Role section — promote/demote between MEMBER and TENANT_ADMIN with a
 * two-click inline confirm. Read-only for a SUPER_ADMIN target or the admin's
 * own row (both blocked by the API, so the UI never offers the action).
 */
/**
 * Admin roles — what this user may OPERATE in the portal, as opposed to scopes,
 * which are what their data and the assistant can reach. Clint gets Knowledge
 * Base Admin without connections or API keys; that separation is the whole
 * reason roles are additive rather than a ladder.
 *
 * Same save shape as the scopes tab: tick, then Save. A checkbox that writes on
 * every click makes an accidental untick a live revocation.
 */
const AdminRolesSection = ({ user, isSelf }: { user: User; isSelf: boolean }) => {
  const logic = useUserAdminRolesLogic(user.id);
  const baseline = useMemo(
    () => logic.roles.filter((r) => r.assigned).map((r) => r.key),
    [logic.roles],
  );
  const [selected, setSelected] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  // Null until the operator touches something, so the list tracks the server
  // until then — no effect needed to sync it after the query resolves.
  const current = selected ?? baseline;
  const dirty = [...baseline].sort().join(',') !== [...current].sort().join(',');

  const save = async () => {
    try {
      await logic.saveRoles(current);
      setSelected(null);
      setSaved(true);
    } catch {
      // surfaced via banner
    }
  };

  return (
    <section className="bg-surface-elevated rounded-lg border border-outline p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-ink text-sm font-semibold">Admin roles</h3>
          <p className="text-ink-muted text-sm">
            Which admin pages this user can open and operate. Roles do not change what the assistant
            can answer for them — that is Scopes.
          </p>
        </div>
        <Button onClick={() => void save()} disabled={logic.isSubmitting || !dirty}>
          {logic.isSubmitting ? 'Saving…' : 'Save roles'}
        </Button>
      </div>
      {logic.error ? (
        <Banner variant="danger" className="mb-3">
          <Banner.Body>
            <Banner.Title>{logic.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      ) : (
        saved &&
        !dirty && (
          <Banner variant="success" className="mb-3">
            <Banner.Body>
              <Banner.Title>Roles saved.</Banner.Title>
            </Banner.Body>
          </Banner>
        )
      )}
      <RoleSelector
        isSelf={isSelf}
        roles={logic.roles}
        selectedRoles={current}
        onSelectedRolesChange={(next) => {
          setSelected(next);
          setSaved(false);
        }}
        isLoading={logic.isLoading}
        isDisabled={logic.isSubmitting}
      />
    </section>
  );
};

const shortDate = (iso: string | null | undefined): string =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'Never';

/**
 * Who this person is. The page header carries their name and status, so this
 * is the detail underneath it.
 *
 * The Entra object id is deliberately NOT shown: the users service excludes
 * externalId from every response on purpose.
 */
const IdentitySection = ({ user }: { user: User }) => {
  const facts: { label: string; value: string }[] = [
    { label: 'Email', value: user.email },
    { label: 'Added', value: shortDate(user.createdAt) },
    { label: 'Last sign-in', value: shortDate(user.lastLoginAt) },
  ];

  return (
    <section className="bg-surface-elevated rounded-lg border border-outline p-4">
      <h3 className="text-ink mb-4 text-sm font-semibold">Identity</h3>
      {/* Label above value, left aligned: each cell is one self-contained fact,
          so the grid can reflow without pairing unrelated fields across a gap. */}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-5">
        {facts.map((f) => (
          <div key={f.label}>
            <dt className="text-ink-subtle text-xs">{f.label}</dt>
            <dd className="text-ink mt-0.5 text-sm">{f.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
};

const GroupsSection = ({ tenantId, user }: { tenantId: string; user: User }) => {
  const logic = useUserGroupsLogic(tenantId, user.id);
  const allGroups = useGroupsListLogic(tenantId);
  const [pick, setPick] = useState<string[]>([]);
  const groups = user.groups ?? [];

  // Only offer groups the user isn't already in.
  const memberIds = new Set(groups.map((g) => g.id));
  const options = (allGroups.data ?? [])
    .filter((g) => !memberIds.has(g.id))
    .map((g) => ({ value: g.id, label: g.name }));
  const pickedId = pick[0];

  const add = async () => {
    if (!pickedId) return;
    await logic.addGroup(pickedId).catch(() => undefined);
    setPick([]);
  };

  return (
    <section className="bg-surface-elevated rounded-lg border border-outline p-4">
      <h3 className="text-ink mb-1 text-sm font-semibold">Groups</h3>
      <p className="text-ink-muted mb-3 text-sm">
        Groups this user belongs to. Adding one grants its scopes; removing one revokes them.
      </p>
      {logic.error && (
        <Banner variant="danger" className="mb-3">
          <Banner.Body>
            <Banner.Title>{logic.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}
      {groups.length === 0 ? (
        <p className="text-ink-subtle mb-3 text-sm">Not a member of any group.</p>
      ) : (
        <div className="divide-y divide-outline mb-3 overflow-hidden rounded-lg border border-outline">
          {groups.map((g) => (
            <div key={g.id} className="flex items-center justify-between px-4 py-2">
              <span className="text-ink text-sm">{g.name}</span>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void logic.removeGroup(g.id)}
                disabled={logic.isSubmitting}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Select
          options={options}
          value={pick}
          onChange={setPick}
          placeholder={options.length === 0 ? 'In every group' : 'Add to a group…'}
          aria-label="Add to a group"
          disabled={options.length === 0 || logic.isSubmitting}
          className="min-w-56"
        />
        <Button size="sm" onClick={() => void add()} disabled={!pickedId || logic.isSubmitting}>
          {logic.isSubmitting ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </section>
  );
};

/**
 * Teams section — push the SC Assistant app to this user (Graph install; the
 * user does nothing). Success = they're DM-able within seconds. PENDING users
 * can't be targeted (no directory identity yet) — the API says so; the UI
 * just relays it.
 */
const TeamsAppSection = ({ tenantId, user }: { tenantId: string; user: User }) => {
  const logic = useTeamsAppLogic(tenantId, user.id);

  return (
    <section className="bg-surface-elevated rounded-lg border border-outline p-4">
      <h3 className="text-ink mb-1 text-sm font-semibold">Teams messages</h3>
      <p className="text-ink-muted mb-3 text-sm">
        Install the SC Assistant app in this user&apos;s Teams so the platform can send them direct
        messages. They don&apos;t need to do anything.
      </p>
      <div className="flex items-center gap-3">
        {logic.isLoading ? (
          <Skeleton className="h-8 w-64" />
        ) : logic.installed ? (
          <>
            <span className="text-sm font-medium text-success">
              {logic.freshInstall
                ? 'Installed — messages can reach them in a few seconds.'
                : 'Installed — they can receive messages.'}
            </span>
            <Button
              variant="ghost"
              size="sm"
              aria-label="Remove Teams app"
              onClick={() => void logic.remove()}
              disabled={logic.isWorking}
            >
              {logic.isWorking ? 'Removing…' : 'Remove'}
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void logic.install()}
            disabled={logic.isWorking}
          >
            {logic.isWorking ? 'Installing…' : 'Install Teams app'}
          </Button>
        )}
        {logic.error && <span className="text-sm text-danger">{logic.error.message}</span>}
      </div>
    </section>
  );
};

const UserManageBody = ({
  tenantId,
  user,
  currentUserId,
}: {
  tenantId: string;
  user: User;
  currentUserId: string | undefined;
}) => {
  const [tab, setTab] = useState<'details' | 'roles'>('details');

  return (
    <>
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-ink text-2xl font-bold">{user.name ?? user.email}</h2>
        <Badge variant={statusVariant[user.status]}>{user.status}</Badge>
      </div>

      <div className="mb-4 flex gap-1 border-b border-outline">
        <TabButton active={tab === 'details'} onClick={() => setTab('details')}>
          Details
        </TabButton>
        <TabButton active={tab === 'roles'} onClick={() => setTab('roles')}>
          Roles
        </TabButton>
      </div>

      {tab === 'details' && (
        <div className="space-y-4">
          <IdentitySection user={user} />
          <GroupsSection tenantId={tenantId} user={user} />
          <TeamsAppSection tenantId={tenantId} user={user} />
        </div>
      )}
      {/* No account type: everyone is a user, and tenant administration is a
          role in the list like any other. */}
      {tab === 'roles' && <AdminRolesSection user={user} isSelf={user.id === currentUserId} />}
    </>
  );
};

export const UserManagePage = () => {
  const { userId } = useParams<{ userId: string }>();
  const meQuery = useMe();
  const tenantId = meQuery.data?.tenant.id;
  const { data: user, isLoading, error } = useUserDetailLogic(tenantId, userId);

  return (
    <AppShell>
      <Breadcrumbs trail={[{ label: 'Users', to: '/users' }]} />

      {error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>{error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {(meQuery.isLoading || isLoading || !user || !tenantId) && !error ? (
        <div className="space-y-4" role="status" aria-label="Loading user">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        user &&
        tenantId && (
          <UserManageBody tenantId={tenantId} user={user} currentUserId={meQuery.data?.user.id} />
        )
      )}
    </AppShell>
  );
};
